import { useEffect, useState } from 'react';
import { Check, Languages, Loader2, MessageCircle, TriangleAlert, UserRound } from 'lucide-react';
import { formatarTelefone, linkWhatsApp, somenteDigitos, telefoneValido } from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Campo } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/auth/AuthContext';
import { api, type ConfiguracaoIA } from '@/lib/api';
import { FormularioDeConfiguracao } from '@/documento/TradutorIa';

/**
 * Minha conta.
 *
 * O telefone é o dado que o site mais precisa manter em dia: é por ele que o
 * suporte fala com o cliente e que o grupo de avisos é montado. Por isso a
 * tela existe separada do cadastro — quem entrou antes do campo existir
 * consegue completar o número aqui.
 */
export function MinhaConta() {
  const { usuario, salvarPerfil } = useAuth();

  const [nome, setNome] = useState(usuario?.nome ?? '');
  const [telefone, setTelefone] = useState(formatarTelefone(usuario?.telefone ?? ''));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  /** A configuração da IA (provedor, modelo e se há chave — a chave nunca volta). */
  const [ia, setIa] = useState<ConfiguracaoIA | null>(null);

  // A conta chega depois do primeiro render (o perfil é carregado pela API).
  useEffect(() => {
    setNome(usuario?.nome ?? '');
    setTelefone(formatarTelefone(usuario?.telefone ?? ''));
  }, [usuario?.nome, usuario?.telefone]);

  // A configuração da IA é de cada usuário: aparece para quem tem o editor de documentos.
  useEffect(() => {
    if (!usuario?.editorDeDocumentos) return;
    void (async () => {
      try {
        setIa(await api.configuracaoIA());
      } catch {
        setIa(null);
      }
    })();
  }, [usuario?.editorDeDocumentos]);

  const telefonePreenchido = telefone.trim().length > 0;
  const telefoneCerto = telefoneValido(telefone);
  const semTelefone = !telefonePreenchido;
  /** Só oferecemos a conversa quando existe um número válido para chamar. */
  const conversa = linkWhatsApp(
    telefoneCerto ? telefone : null,
    'Olá! Preciso de ajuda com o meu miolo de agenda.',
  );

  /** Comparação pelos dígitos: máscara e espaços não contam como alteração. */
  const alterou =
    nome.trim() !== (usuario?.nome ?? '') || somenteDigitos(telefone) !== (usuario?.telefone ?? '');

  const submeter = async (evento: React.FormEvent) => {
    evento.preventDefault();
    setErro(null);
    setSalvo(false);

    if (nome.trim().length < 2) {
      setErro('Informe seu nome.');
      return;
    }
    if (telefonePreenchido && !telefoneCerto) {
      setErro('Telefone inválido. Use o formato (11) 91234-5678.');
      return;
    }

    setSalvando(true);
    try {
      // Vazio apaga o número: é assim que a pessoa tira o telefone da conta.
      await salvarPerfil({ nome: nome.trim(), telefone: telefonePreenchido ? telefone : null });
      setSalvo(true);
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível salvar agora.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Minha conta</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Seus dados de contato. O e-mail de acesso não muda por aqui.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserRound className="size-4 text-primary" />
            Dados de contato
          </CardTitle>
          <CardDescription>
            O telefone é o que usamos para falar com você sobre a sua agenda — e o que entra no grupo de
            avisos.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submeter}>
            <Campo rotulo="Seu nome">
              <Input
                required
                value={nome}
                onChange={(e) => {
                  setNome(e.target.value);
                  // Mexeu de novo: o aviso de “salvo” deixa de valer.
                  setSalvo(false);
                }}
                placeholder="Como devo te chamar"
              />
            </Campo>

            <Campo rotulo="Telefone / WhatsApp" dica="Com DDD. Deixe em branco para remover o número.">
              <Input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                maxLength={15}
                value={telefone}
                onChange={(e) => {
                  setTelefone(formatarTelefone(e.target.value));
                  setSalvo(false);
                }}
                placeholder="(11) 91234-5678"
              />
            </Campo>

            <div className="flex flex-wrap items-center gap-2 text-xs">
              {telefoneCerto ? (
                <Badge tom="sucesso">
                  <Check className="size-3" />
                  {formatarTelefone(telefone)}
                </Badge>
              ) : semTelefone ? (
                <Badge tom="atencao">
                  <TriangleAlert className="size-3" />
                  Sem telefone
                </Badge>
              ) : (
                <Badge tom="neutro">Número incompleto</Badge>
              )}
              {conversa ? (
                <a
                  className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
                  href={conversa}
                  target="_blank"
                  rel="noreferrer"
                >
                  <MessageCircle className="size-3.5" />
                  Abrir conversa no WhatsApp
                </a>
              ) : null}
            </div>

            <Campo rotulo="E-mail de acesso">
              <Input value={usuario?.email ?? ''} readOnly disabled />
            </Campo>

            {erro ? <p className="text-xs text-destructive">{erro}</p> : null}
            {salvo ? <p className="text-xs text-success">Dados salvos.</p> : null}

            <div className="flex items-center gap-3">
              <Button type="submit" disabled={salvando || !alterou}>
                {salvando ? <Loader2 className="animate-spin" /> : <Check />}
                Salvar alterações
              </Button>
              {salvo ? <span className="text-xs text-muted-foreground">Tudo em dia.</span> : null}
            </div>
          </form>
        </CardContent>
      </Card>

      {usuario?.editorDeDocumentos ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Languages className="size-4 text-primary" />
              IA para traduzir
            </CardTitle>
            <CardDescription>
              Escolha qual IA usar para traduzir resumos e palavras-chave dos documentos acadêmicos. A
              chave é sua e fica guardada só para a sua conta — o site nunca a mostra de volta.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FormularioDeConfiguracao
              config={ia}
              aoSalvar={async () => {
                setIa(await api.configuracaoIA());
              }}
            />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
